/*
  Таблица судов справочника: участки мировых судей или районные суды.

  🔴 Решение владельца 14.09.2026: было по карточке на каждый суд, и каждая
  занимала пол-экрана - десяток участков не помещался никуда, а сравнить их
  между собой (у кого указан район, у кого нет домов) было нельзя вовсе. Таблица
  читается одним взглядом, а работа с домами открывается по выбору строки.

  Две таблицы вместо одной с колонкой вида: у района нет ни номера, ни судьи, ни
  домов, и половина колонок стояла бы пустой. Пустая колонка читается как
  «данные потеряли».
*/

import { Lock } from './AccessContext.jsx'
import { Empty, Loading } from './Common.jsx'
import PageBar from './PageBar.jsx'
import Dropdown from './Select.jsx'

/*
  `list` - результат `usePagedList` для этого вида судов: строки страницы приходят с
  сервера (21.10.2026, пагинация серверная), под таблицей - общая панель. Выбор участка
  (`selectedId`) хранится в родителе и от страницы не зависит: выбранная строка остаётся
  выбранной. `districts` - все районные суды для выбора в строке участка (выпадающий
  список страниц не получает).
*/
export default function CourtTable({ list, districts, selectedId, onSelect, onSetDistrict, onDelete }) {
  const isDistrict = !onSetDistrict
  const { rows: courts, bar } = list

  if (courts === null) return <Loading what="Читаем справочник судов" />
  if (courts.length === 0) {
    return isDistrict ? (
      <Empty title="Районных судов пока нет">
        Иск подаётся не мировому судье, а в районный суд. Заведите районные суды и укажите
        каждому участку свой - тогда адресат иска определится по адресу помещения так же, как
        участок для приказа.
      </Empty>
    ) : (
      <Empty title="Участков пока нет">
        Заведите участок и отметьте дома, которые к нему относятся, - дальше участок будет
        подставляться в заявление по адресу помещения.
      </Empty>
    )
  }

  return (
    <>
      <table className={`table table--fixed ${isDistrict ? 'table--court-districts' : 'table--court-list'}`}>
        <thead>
          <tr>
            {isDistrict ? null : <th className="table__num">№</th>}
            <th>Наименование</th>
            {isDistrict ? <th>Адрес суда</th> : <th>Судья</th>}
            {isDistrict ? <th className="table__num">Участков</th> : null}
            {isDistrict ? null : <th>Районный суд</th>}
            {isDistrict ? null : <th className="table__num">Домов</th>}
            {isDistrict ? null : <th className="table__num">Помещений</th>}
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {courts.map((court) => (
            <tr
              key={court.id}
              className={court.id === selectedId ? 'row--selected' : undefined}
            >
              {isDistrict ? null : (
                <td className="table__num" data-label="№">
                  {court.number}
                </td>
              )}
              {/* Первая ячейка подписи не носит: наименование читается само и
                  служит заголовком карточки на узком экране (правило 6е). */}
              <td>
                {isDistrict ? (
                  court.name
                ) : (
                  <button
                    className="link-button"
                    type="button"
                    onClick={() => onSelect(court.id === selectedId ? null : court)}
                  >
                    {court.name}
                  </button>
                )}
              </td>
              {isDistrict ? (
                <td data-label="Адрес суда">{court.court_address}</td>
              ) : (
                <td data-label="Судья">{court.judge_name}</td>
              )}
              {isDistrict ? (
                <td className="table__num" data-label="Участков">
                  {court.sections_count}
                </td>
              ) : null}
              {isDistrict ? null : (
                <td className="table__block" data-label="Районный суд">
                  <Lock>
                    <Dropdown
                      value={court.district_court_id ?? ''}
                      onChange={(value) => onSetDistrict(court, value)}
                      options={Object.fromEntries(districts.map((row) => [row.id, row.name]))}
                      empty="районный суд не указан"
                      labelledBy={`district-${court.id}`}
                    />
                  </Lock>
                  <span className="visually-hidden" id={`district-${court.id}`}>
                    Районный суд - адресат иска, участок № {court.number}
                  </span>
                </td>
              )}
              {isDistrict ? null : (
                <td className="table__num" data-label="Домов">
                  {court.buildings_count}
                </td>
              )}
              {isDistrict ? null : (
                <td className="table__num" data-label="Помещений">
                  {court.premises_count}
                </td>
              )}
              <td>
                <Lock>
                  <button className="button button--quiet" type="button" onClick={() => onDelete(court)}>
                    Удалить
                  </button>
                </Lock>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what={isDistrict ? 'районных судов' : 'участков'} />
    </>
  )
}
